import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import LiveFundTable from '@/components/coveredCall/LiveFundTable';
import LiveVersusPanel from '@/components/coveredCall/LiveVersusPanel';

// Stable reference — a fresh array literal would refetch every render.
const MAJOR_PLAYERS = ['JEPI', 'JEPQ', 'QYLD', 'XYLD', 'DIVO', 'SPYI'];

const FAQS = [
  {
    q: 'What is a covered call ETF?',
    a: 'A covered call ETF owns a basket of stocks and sells call options against them every month (or every week). The option premium becomes income, distributed to shareholders monthly. The trade-off: when stocks rip upward, the sold calls cap some of that upside. When stocks fall or chop sideways, the premium income cushions the decline.',
  },
  {
    q: 'Are covered call ETFs safe for retirees?',
    a: 'Safer than the underlying basket in flat or down markets — yes. Safer than US treasuries — no. Covered call ETFs are still equity-risk products that fall when the market falls (they just fall less). Use them as a SLICE of an income portfolio, not the whole thing. Pair with bonds, treasuries, and traditional dividend stocks for diversification.',
  },
  {
    q: 'Do covered call ETF distributions count as qualified dividends?',
    a: 'Mostly no. The option-premium component is treated as ordinary income (your full marginal tax rate) plus a small amount of section 1256 60/40 treatment. Some funds also pay through "return of capital" which reduces your cost basis instead of being taxed immediately. Always check the 1099-DIV — the qualified portion varies year to year. In a Roth or traditional IRA, none of this matters.',
  },
  {
    q: 'Why do covered call ETF yields fluctuate so much month-to-month?',
    a: 'The yield is option premium, and option premium is priced off implied volatility. When VIX is high (scared market), premiums are fat and distributions are big. When VIX collapses (calm market), premiums shrink and distributions shrink with them. Plan for variance — these funds NEVER promise a constant yield.',
  },
  {
    q: 'Should I DRIP a covered call ETF?',
    a: 'For accumulators, yes — reinvesting the monthly distribution keeps compounding the share count. For retirees living off the income, no — take the cash. DiviDrip lets you log each lot as either a regular Buy or a DRIP buy and tracks them separately for honest cost-basis math.',
  },
];

export default function LearnCoveredCallEtfs() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="Covered Call ETFs Explained — JEPI, JEPQ, QYLD, XYLD & More (2026) | DiviDrip"
      description="What covered call ETFs are, how they generate 8-12% yields, the trade-offs vs traditional dividend funds, and a side-by-side of the major players: JEPI, JEPQ, QYLD, XYLD, DIVO, SPYI."
      canonical="https://twylightcrow.com/learn/covered-call-etfs"
      guidePath="/learn/covered-call-etfs"
      sectionLabel="Vehicles & Structures"
      sectionAnchor="vehicles"
      kicker="Learn · Income ETFs"
      h1="Covered Call ETFs — How They Generate Double-Digit Yields"
      datePublished="2026-05-22"
      jsonLd={jsonLd}
      related={[
        { to: '/covered-call-etfs', label: 'Covered call ETF list — live yields vs true total return' },
        { to: '/learn/build-5k-monthly-income', label: 'How to build a $5K monthly income portfolio' },
        { to: '/learn/monthly-dividend-calendar', label: 'The monthly dividend calendar trick' },
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs vs BDCs vs CEFs — high-yield vehicles compared' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — tax guide' },
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/jepi-vs-jepq', label: 'JEPI vs JEPQ — covered-call yield face-off' },
        { to: '/learn/schd-vs-voo', label: 'SCHD vs VOO — dividend ETF vs total-market ETF' },
        { to: '/screener', label: 'Stock Screener — filter ETFs by yield, sector, frequency' },
      ]}
    >
      <p>
        Walk into any dividend forum in 2026 and you&apos;ll see the same question over and
        over: <em>&ldquo;How are these ETFs paying 9, 10, even 12% yields?&rdquo;</em> The
        answer in two words: <strong>covered calls</strong>. Covered call ETFs are a
        decade-old strategy that got mainstream when JPMorgan launched JEPI in 2020, and
        the category has exploded. Here&apos;s how they actually work, what they cost you
        in trade-offs, and how the big six compare.
      </p>

      <h2>The mechanic</h2>
      <p>
        Imagine you own 100 shares of Microsoft trading at $400. You promise to sell those
        shares to someone next month at $420 (a &ldquo;strike price&rdquo; above today). In
        exchange for that promise, that person pays you a premium — say $5 per share, $500
        total — right now. That $500 is yours to keep no matter what happens. Three things
        can play out next month:
      </p>
      <ol>
        <li><strong>MSFT below $420:</strong> the buyer walks away; you keep your shares AND the $500.</li>
        <li><strong>MSFT exactly $420:</strong> you sell at $420 and keep the $500. You made $20/share + $5/share = $2,500 total.</li>
        <li><strong>MSFT above $420 (e.g. $450):</strong> you still sell at $420. You miss the $30/share above strike. Net: $20/share + $5/share = $2,500.</li>
      </ol>
      <p>
        Now multiply that by a basket of 100+ stocks, repeated every month, and you have a
        covered call ETF. The premiums become the &ldquo;dividend&rdquo; that gets
        distributed to shareholders.
      </p>

      <h2>The trade-off you must understand</h2>
      <p>
        Covered call ETFs are <strong>income-now, growth-later</strong> products. The sold
        calls cap upside, so in a rip-roaring bull market the fund underperforms its
        underlying basket. In a flat or modestly-up market, the premium income BEATS the
        basket. In a crash, both fall, but the fund falls slightly less because the
        premium dollars cushion the decline.
      </p>

      <h2>The major players — side by side, live</h2>
      <p>
        Yields on these funds move every month with implied volatility, so the table below is
        wired to our nightly data rather than typed in by hand. The last column is the one that
        settles arguments: total return over the past year — share-price change plus every
        distribution actually paid.
      </p>
      <LiveFundTable
        tickers={MAJOR_PLAYERS}
        meta={{
          JEPI: { underlying: 'Low-vol S&P 500', note: "JPM's flagship. Most defensive of the bunch." },
          JEPQ: { underlying: 'Low-vol Nasdaq-100', note: 'Tech-tilted JEPI. Higher yield, higher vol.' },
          QYLD: { underlying: 'Nasdaq-100, at-the-money calls', note: 'Older, more aggressive cap on upside. Price has stagnated.' },
          XYLD: { underlying: 'S&P 500, at-the-money calls', note: "QYLD's S&P 500 sibling." },
          DIVO: { underlying: 'Dividend payers, partial calls', note: 'Hybrid — only writes calls on a portion. Lower yield, more upside.' },
          SPYI: { underlying: 'S&P 500, complex hedge', note: 'Uses index options + put hedge for tax-friendly distributions.' },
        }}
      />

      <h2>See the live numbers — and the returns behind the yields</h2>
      <p>
        The table above covers the six best-known funds. If you want the whole market — all 190
        option-income ETFs, including every YieldMax single-stock fund and the 0DTE weekly payers —
        and the ability to sort them yourself, we maintain a live research page:{' '}
        <Link to="/covered-call-etfs">the Covered Call ETF list</Link>.
      </p>
      <p>When you open it, you can:</p>
      <ul>
        <li>
          <strong>Compare 190 option-income ETFs in one table</strong> — every JEPI/JEPQ-style
          fund, all 53 YieldMax single-stock funds, the 0DTE weekly payers (QDTE, XDTE, RDTE),
          the NEOS and Goldman funds, and the Global X classics.
        </li>
        <li>
          <strong>See true 1-year total return, not just the headline yield</strong> — share-price
          change plus every distribution actually paid. This is the column that exposes a fund
          advertising a 100%+ yield while quietly handing your capital back to you.
        </li>
        <li>
          <strong>Spot NAV erosion instantly</strong> — a dedicated 1-year price-change column,
          plus a count of how many funds finished the last 12 months underwater despite their
          enormous distributions.
        </li>
        <li>
          <strong>Sort and filter</strong> by yield, dollars paid per share over the last 12
          months, expense ratio, payment frequency, or fund size — and filter to a single family
          (YieldMax, Roundhill, First Trust, Global X and more).
        </li>
        <li>
          <strong>Open a head-to-head page</strong> for the matchups people actually argue about
          — <Link to="/msty-vs-ymax">MSTY vs YMAX</Link>,{' '}
          <Link to="/ulty-vs-jepq">ULTY vs JEPQ</Link>,{' '}
          <Link to="/qdte-vs-xdte">QDTE vs XDTE</Link>,{' '}
          <Link to="/spyi-vs-jepi">SPYI vs JEPI</Link> — each with a verdict, the total-return
          arithmetic worked out side by side, the last 12 distributions charted, and the fee and
          payout-trend warnings that matter.
        </li>
      </ul>
      <p>
        Everything on it refreshes with our nightly pipeline, so the yields, prices and payout
        histories reflect the most recent close rather than a fund fact sheet from last quarter.
      </p>
      <p>
        <Link to="/covered-call-etfs">
          <strong>Open the live Covered Call ETF list and comparison hub →</strong>
        </Link>
      </p>


      <h2>How to think about position sizing</h2>
      <p>
        For most investors, covered call ETFs should be a <strong>15-30% slice</strong> of
        the income sleeve of a portfolio, not the whole thing. They&apos;re wonderful at
        producing immediate cashflow but they trade away the long-tail growth that pure
        dividend-growers (Kings, Aristocrats) and broad-market ETFs (VOO, VTI) provide.
        A balanced approach: 40% dividend growers + 30% broad market + 30% covered call
        income.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <p className="text-xs text-muted-foreground italic mt-6">
        Disclaimer: Not investment advice. Yields drift with implied volatility; past
        distributions are not promises about future distributions. Always check the
        fund&apos;s latest factsheet before buying.
      </p>
    </LearnPage>
  );
}
